import React, { useState } from "react";
import { Twitter, Linkedin, Link as LinkIcon, MessageSquare, Instagram, Check, Download } from "lucide-react";
import { API } from "../lib/api";

/**
 * SharePanel — attaches to a closed WINNING alert row on the subscriber
 * dashboard. Turns the win into a share-ready card + deep links to Twitter,
 * LinkedIn, Discord, and Instagram (mobile deep-link / desktop download).
 *
 * v1 avoids Meta / X OAuth — we open web intents in a new tab and let the
 * user paste the pre-filled copy. The image is fetched from
 * `/api/win/{id}/card.png` (public, cached).
 */
export default function SharePanel({ alert, refCode }) {
  const [copied, setCopied] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const id = alert.id;
  const ret = alert.live?.return_pct ?? alert.final_return_pct ?? 0;
  const symbol = alert.symbol;

  const publicOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const ref = refCode ? `?ref=${refCode}` : "";
  const winUrl = `${publicOrigin}/win/${id}${ref}`;
  const cardUrl = `${API}/win/${id}/card.jpg`;

  const copy = `${symbol} closed +${Number(ret).toFixed(1)}% — vetted daily by the @moonbucksapp desk. See what's on the desk right now:`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(copy)}&url=${encodeURIComponent(winUrl)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(winUrl)}`;
  const discordShare = `${copy}\n${winUrl}`;

  const isMobile = typeof navigator !== "undefined" && /iPhone|iPad|Android/i.test(navigator.userAgent);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(winUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked, ignore */ }
  };

  const copyDiscord = async () => {
    try {
      await navigator.clipboard.writeText(discordShare);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const downloadCard = async () => {
    setDownloading(true);
    try {
      const resp = await fetch(cardUrl);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `moonbucks-${symbol}-${id.slice(0, 8)}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { /* ignore */ }
    setDownloading(false);
  };

  const handleInstagram = async () => {
    // Instagram has no web share intent. On mobile, try the Stories deep
    // link (requires the app installed + the image on disk). On desktop,
    // just download the card so the user can DM / post manually.
    await downloadCard();
    if (isMobile) {
      try { window.location.href = "instagram-stories://share"; } catch { /* ignore */ }
    }
  };

  return (
    <div
      className="mt-3 p-3 rounded-md space-y-3"
      style={{
        backgroundColor: "var(--mb-surface)",
        border: "1px solid var(--mb-border)",
      }}
      data-testid={`share-panel-${symbol}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--mb-ink-muted)" }}>
          Share this win
        </div>
        <a
          href={winUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] font-mono-data tracking-wide underline"
          style={{ color: "var(--mb-ink-subtle)" }}
          data-testid={`share-preview-${symbol}`}
        >
          Preview page →
        </a>
      </div>

      {!imgError && (
        <a
          href={winUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded-md overflow-hidden border"
          style={{ borderColor: "var(--mb-border)" }}
        >
          <img
            src={cardUrl}
            alt={`${symbol} win card`}
            className="w-full h-auto block"
            style={{ aspectRatio: "1200/630", backgroundColor: "#FBF7EC" }}
            onError={() => setImgError(true)}
            loading="lazy"
            data-testid={`share-card-img-${symbol}`}
          />
        </a>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <a
          href={twitterUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-md border transition hover:opacity-80"
          style={{ backgroundColor: "var(--mb-surface-elevated)", borderColor: "var(--mb-border)" }}
          title="Share to Twitter / X"
          data-testid={`share-twitter-${symbol}`}
        >
          <Twitter className="w-4 h-4" style={{ color: "#1DA1F2" }} />
        </a>
        <a
          href={linkedinUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-md border transition hover:opacity-80"
          style={{ backgroundColor: "var(--mb-surface-elevated)", borderColor: "var(--mb-border)" }}
          title="Share to LinkedIn"
          data-testid={`share-linkedin-${symbol}`}
        >
          <Linkedin className="w-4 h-4" style={{ color: "#0A66C2" }} />
        </a>
        <button
          onClick={copyDiscord}
          className="p-2 rounded-md border transition hover:opacity-80"
          style={{ backgroundColor: "var(--mb-surface-elevated)", borderColor: "var(--mb-border)" }}
          title="Copy Discord-ready message"
          data-testid={`share-discord-${symbol}`}
        >
          <MessageSquare className="w-4 h-4" style={{ color: "#5865F2" }} />
        </button>
        <button
          onClick={handleInstagram}
          className="p-2 rounded-md border transition hover:opacity-80"
          style={{ backgroundColor: "var(--mb-surface-elevated)", borderColor: "var(--mb-border)" }}
          title={isMobile ? "Open Instagram Stories" : "Download card for Instagram"}
          data-testid={`share-instagram-${symbol}`}
        >
          <Instagram className="w-4 h-4" style={{ color: "#E1306C" }} />
        </button>
        <button
          onClick={downloadCard}
          disabled={downloading}
          className="p-2 rounded-md border transition hover:opacity-80"
          style={{ backgroundColor: "var(--mb-surface-elevated)", borderColor: "var(--mb-border)" }}
          title="Download PNG"
          data-testid={`share-download-${symbol}`}
        >
          <Download className="w-4 h-4" style={{ color: "var(--mb-ink-muted)" }} />
        </button>
        <button
          onClick={copyLink}
          className="ml-auto px-3 py-2 rounded-md border transition flex items-center gap-1.5 text-xs font-mono-data hover:opacity-80"
          style={{
            backgroundColor: "var(--mb-surface-elevated)",
            borderColor: "var(--mb-border)",
            color: copied ? "#4ADE80" : "var(--mb-ink-muted)",
            maxWidth: "60%",
          }}
          title="Copy share link"
          data-testid={`share-copy-${symbol}`}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <LinkIcon className="w-3.5 h-3.5" />}
          <span className="truncate">{copied ? "Copied" : `/win/${id.slice(0, 8)}${ref}`}</span>
        </button>
      </div>
    </div>
  );
}
